import { Button, Flex } from "@mantine/core";
import { Calendar, CalendarProps } from "@mantine/dates";
import * as React from "react";

import dayjs, { Dayjs } from "~/utils/dayjs";

import { isInSameWeek } from "./weekFns";

const WeekPicker = ({
  defaultValue,
  onConfirm,
  onCancel,
}: {
  defaultValue?: Dayjs;
  onConfirm: (value: Dayjs) => void;
  onCancel?: () => void;
}) => {
  const [hoveredDay, setHoveredDay] = React.useState<Date | null>(null);
  const [value, setValue] = React.useState<Date>(
    defaultValue?.toDate() || new Date(),
  );

  const getDayProps: CalendarProps["getDayProps"] = (date: string) => {
    const selectedDay = dayjs(value);
    const hoveredDayjs = hoveredDay ? dayjs(hoveredDay) : null;
    const today = dayjs();

    const dateObj = new Date(date); // Convert string to Date for existing functions

    const isSelected = isInSameWeek(dateObj, selectedDay);
    const isHovered = isInSameWeek(dateObj, hoveredDayjs);
    const isToday = dayjs(date).isSame(today, "day");
    const dayOfWeek = dayjs(date).day();

    return {
      onMouseEnter: () => setHoveredDay(dateObj),
      onMouseLeave: () => setHoveredDay(null),
      onClick: () => setValue(dateObj),
      inRange: isSelected || isHovered,
      firstInRange: (isSelected || isHovered) && dayOfWeek === 1, // Monday
      lastInRange: (isSelected || isHovered) && dayOfWeek === 0, // Sunday
      selected: false, // Don't use single-day selection
      style: {
        // Override default range styling to differentiate selected vs hovered
        ...(isSelected && {
          backgroundColor: "var(--mantine-primary-color-filled)", // Bright color for selected
          color: "var(--mantine-primary-color-contrast)",
          borderRadius: 0, // Reset border radius for middle days
          // Round corners for start/end of selected week
          ...(dayOfWeek === 1 && {
            borderTopLeftRadius: "50%",
            borderBottomLeftRadius: "50%",
          }),
          ...(dayOfWeek === 0 && {
            borderTopRightRadius: "50%",
            borderBottomRightRadius: "50%",
          }),
        }),
        ...(isHovered &&
          !isSelected && {
            backgroundColor: "var(--mantine-primary-color-light)", // Light color for hover
            borderRadius: 0, // Reset border radius for middle days
            // Round corners for start/end of hovered week
            ...(dayOfWeek === 1 && {
              borderTopLeftRadius: "50%",
              borderBottomLeftRadius: "50%",
            }),
            ...(dayOfWeek === 0 && {
              borderTopRightRadius: "50%",
              borderBottomRightRadius: "50%",
            }),
          }),
        // Ensure today border always shows on top
        ...(isToday && {
          border: "1px solid var(--mantine-color-text)",
          boxSizing: "border-box",
        }),
      },
    };
  };

  return (
    <>
      <Calendar
        date={value}
        onDateChange={(newValue) => setValue(new Date(newValue))}
        firstDayOfWeek={1} // Monday
        getDayProps={getDayProps}
        hideWeekdays={false}
        weekendDays={[]} // Disable weekend styling
        hideOutsideDates={false}
        withWeekNumbers
        highlightToday
        styles={{
          monthCell: {
            border: "none",
            padding: "0",
          },
        }}
      />
      <Flex justify="flex-end" gap="xs" style={{ marginTop: "8px" }}>
        {onCancel && (
          <Button variant="subtle" size="sm" onClick={() => onCancel?.()}>
            Cancel
          </Button>
        )}
        <Button
          variant="subtle"
          size="sm"
          onClick={() => {
            onConfirm(dayjs(value));
            onCancel?.();
          }}
        >
          Apply
        </Button>
      </Flex>
    </>
  );
};

export default WeekPicker;
